import { Viewport } from 'pixi-viewport';
import { FederatedPointerEvent, Point } from 'pixi.js';
import { animationFrameScheduler, filter, interval, map, Subject, takeUntil } from 'rxjs';

import Movement from './movement';
import { Player } from './player';
import { SceneManager } from './scene-manager';
import { IScene } from './types';

export default class MouseMovementController {
  private static instance: MouseMovementController;
  public viewport?: Viewport;
  public movementEnabled?: boolean;
  public isMobile?: boolean;

  private isHoldingPointerDown?: boolean;
  private lastMousePos?: Partial<Point>;
  private movement: Movement;

  private scene?: IScene;
  private movementInterval$: Subject<void> = new Subject<void>();

  public constructor(private sceneManager: SceneManager) {
    this.movement = Movement.getInstance(this.sceneManager);
  }

  public static getInstance(sceneManager: SceneManager): MouseMovementController {
    if (!MouseMovementController.instance) {
      MouseMovementController.instance = new MouseMovementController(sceneManager);
    }
    return MouseMovementController.instance;
  }

  /**
   * Creates an interval that checks if the mouse click is being held by the user and recalculates the new mouse position
   * because viewport position may be different at this point. Then it moves to the new position.
   */
  public initMovementListener({
    intervalTimeFrame,
    isMobile,
    scene,
  }: {
    intervalTimeFrame: number;
    scene: IScene;
    isMobile: boolean;
  }): void {
    this.scene = scene;
    this.isMobile = isMobile;
    this.movementEnabled = !isMobile;
    interval(intervalTimeFrame, animationFrameScheduler)
      .pipe(
        filter(() => !!this.isHoldingPointerDown && !!this.lastMousePos && !!this.movementEnabled),
        map(() => this.lastMousePos),
        filter((pos) => pos !== null && pos!.x !== undefined && pos!.y !== undefined),
        map((pos) => this.getCursorAbsolutePosition(pos!.x!, pos!.y!)),
        takeUntil(this.movementInterval$),
      )
      .subscribe((newPos) => {
        if (!isMobile) {
          this.movement.moveTo({ x: newPos.x, y: newPos.y, moveWithMouse: true });
        }
      });
  }

  /**
   * Adds the viewport position to the mouse position to obtain the absolute position of the cursor
   * @param {number} mouseX - The position of the cursor on the x axis relative to the viewport
   * @param {number} mouseY - The position of the cursor on the y axis relative to the viewport
   * @returns {obj} {x, y} - Absolute coordinates of the cursor in the world (x, y)
   */
  public getCursorAbsolutePosition(mouseX: number, mouseY: number): { x: number; y: number } {
    const { x: viewportX, y: viewportY } = this.viewport?.getVisibleBounds() ?? { x: 0, y: 0 };
    const { zoom } = this.scene ?? { zoom: 1 };
    const x = mouseX / zoom + viewportX;
    const y = mouseY / zoom + viewportY;
    return { x, y };
  }

  /**
   * Uses atan2 function to calculate angle between cursor and player, then it converts the result to degrees.
   * @param {Object} obj - The Parameter object
   * @param {Object} obj.cursorAbsolutePosition - Coordinates object
   * @param {number} obj.cursorAbsolutePosition.x - The absolute position of the cursor on the x axis
   * @param {number} obj.cursorAbsolutePosition.y - The absolute position of the cursor on the x axis
   * @returns {number} The angle between player and cursor
   */
  public getCursorAngle({
    cursorAbsolutePosition,
    player,
  }: {
    cursorAbsolutePosition: { x: number; y: number };
    player: Player;
  }): number {
    const { position: mainPlayerPosition } = player;
    const radians = Math.atan2(
      cursorAbsolutePosition.x - mainPlayerPosition.x,
      cursorAbsolutePosition.y - mainPlayerPosition.y,
    );
    let degrees = radians * (180 / Math.PI);

    // Necessary conversion because pixi has inverted Y axis.
    if (degrees > 0) {
      degrees = degrees - 90;
    } else if (degrees <= 0) {
      degrees = degrees + 270;
    }
    return degrees * -1;
  }

  public onPointerDown(event: FederatedPointerEvent): void {
    if (this.movementEnabled) {
      // only handle left mouse button
      if (event.button !== 0) {
        return;
      }
      this.lastMousePos = event.global;
      this.isHoldingPointerDown = true;
    }
  }

  public onPointerUp(event: FederatedPointerEvent): void {
    if (this.movementEnabled) {
      // only handle left mouse button
      if (event.button !== 0) {
        return;
      }
      this.isHoldingPointerDown = false;
      if (this.movementEnabled) {
        this.movement.moveTo({});
      }
    }
  }

  public onPointerMove(event: FederatedPointerEvent): void {
    if (this.isHoldingPointerDown && this.movementEnabled) {
      this.lastMousePos = event.global;
    }
  }

  public onMouseLeave(): void {
    this.isHoldingPointerDown = false;
  }

  public destroy(): void {
    this.scene = undefined;
    this.movementInterval$.next();
    this.movementInterval$.complete();
  }
}
